import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock,
  Calendar,
  Plus,
  Minus,
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';

const Availability = () => {
  const [weeklySchedule, setWeeklySchedule] = useState({
    monday: { available: true, from: '09:00', to: '18:00' },
    tuesday: { available: true, from: '09:00', to: '18:00' },
    wednesday: { available: true, from: '09:00', to: '18:00' },
    thursday: { available: true, from: '09:00', to: '18:00' },
    friday: { available: true, from: '09:00', to: '18:00' },
    saturday: { available: true, from: '10:00', to: '16:00' },
    sunday: { available: false, from: '10:00', to: '16:00' }
  });

  const [blackoutDates, setBlackoutDates] = useState([
    { id: 1, date: '2024-11-25', reason: 'Thanksgiving Holiday' },
    { id: 2, date: '2024-12-25', reason: 'Christmas Day' },
    { id: 3, date: '2024-01-01', reason: 'New Year Day' }
  ]);

  const [newBlackoutDate, setNewBlackoutDate] = useState({ date: '', reason: '' });
  const [showAddBlackout, setShowAddBlackout] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null, 'saving', 'success', 'error'

  const dayNames = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday'
  };

  const handleScheduleChange = (day, field, value) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
    setHasChanges(true);
  };

  const toggleDayAvailability = (day) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: { ...prev[day], available: !prev[day].available }
    }));
    setHasChanges(true);
  };

  const addBlackoutDate = () => {
    if (newBlackoutDate.date && newBlackoutDate.reason) {
      const newBlackout = {
        id: Date.now(),
        date: newBlackoutDate.date,
        reason: newBlackoutDate.reason
      };
      setBlackoutDates([...blackoutDates, newBlackout]);
      setNewBlackoutDate({ date: '', reason: '' });
      setShowAddBlackout(false);
      setHasChanges(true);
    }
  };

  const removeBlackoutDate = (id) => {
    setBlackoutDates(blackoutDates.filter(date => date.id !== id));
    setHasChanges(true);
  };

  const saveAvailability = async () => {
    setSaveStatus('saving');
    
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSaveStatus('success');
      setHasChanges(false);
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const resetToDefault = () => {
    setWeeklySchedule({
      monday: { available: true, from: '09:00', to: '18:00' },
      tuesday: { available: true, from: '09:00', to: '18:00' },
      wednesday: { available: true, from: '09:00', to: '18:00' },
      thursday: { available: true, from: '09:00', to: '18:00' },
      friday: { available: true, from: '09:00', to: '18:00' },
      saturday: { available: true, from: '10:00', to: '16:00' },
      sunday: { available: false, from: '10:00', to: '16:00' }
    });
    setHasChanges(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/caregiver-dashboard" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Availability Settings</h1>
              <p className="text-gray-600 mt-2">Set your weekly schedule and blackout dates</p>
            </div>
            <div className="flex items-center gap-3">
              {hasChanges && (
                <button
                  onClick={resetToDefault}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors inline-flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              )}
              <button
                onClick={saveAvailability}
                disabled={!hasChanges || saveStatus === 'saving'}
                className={`px-6 py-2 rounded-lg font-medium transition-colors inline-flex items-center gap-2 ${
                  hasChanges && saveStatus !== 'saving'
                    ? 'bg-[var(--pf-accent)] text-white hover:opacity-90'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {saveStatus === 'saving' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Save Status */}
        {saveStatus && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            saveStatus === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200'
              : saveStatus === 'error'
              ? 'bg-red-50 text-red-800 border border-red-200'
              : 'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            {saveStatus === 'success' && <CheckCircle className="w-5 h-5" />}
            {saveStatus === 'error' && <AlertCircle className="w-5 h-5" />}
            <span>
              {saveStatus === 'success' && 'Availability settings saved successfully!'}
              {saveStatus === 'error' && 'Error saving availability settings. Please try again.'}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Weekly Schedule */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Weekly Schedule
            </h2>
            
            <div className="space-y-4">
              {Object.entries(weeklySchedule).map(([day, schedule]) => (
                <div key={day} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-20">
                      <span className="font-medium text-gray-900">{dayNames[day]}</span>
                    </div>
                    <button
                      onClick={() => toggleDayAvailability(day)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        schedule.available ? 'bg-[var(--pf-accent)]' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          schedule.available ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  {schedule.available && (
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={schedule.from}
                        onChange={(e) => handleScheduleChange(day, 'from', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="time"
                        value={schedule.to}
                        onChange={(e) => handleScheduleChange(day, 'to', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  )}
                  
                  {!schedule.available && (
                    <span className="text-gray-500 text-sm">Unavailable</span>
                  )}
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    Object.keys(weeklySchedule).forEach(day => {
                      handleScheduleChange(day, 'available', true);
                    });
                  }}
                  className="text-sm bg-green-100 text-green-700 px-3 py-2 rounded-lg hover:bg-green-200"
                >
                  Enable All Days
                </button>
                <button
                  onClick={() => {
                    ['saturday', 'sunday'].forEach(day => {
                      handleScheduleChange(day, 'available', false);
                    });
                  }}
                  className="text-sm bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200"
                >
                  Weekend Off
                </button>
              </div>
            </div>
          </div>

          {/* Blackout Dates */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Blackout Dates
              </h2>
              <button
                onClick={() => setShowAddBlackout(true)}
                className="pf-btn-primary px-3 py-2 rounded-lg transition-colors inline-flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Date
              </button>
            </div>

            <div className="space-y-3">
              {blackoutDates.length > 0 ? (
                blackoutDates.map((blackout) => (
                  <div key={blackout.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{formatDate(blackout.date)}</p>
                      <p className="text-sm text-gray-600">{blackout.reason}</p>
                    </div>
                    <button
                      onClick={() => removeBlackoutDate(blackout.id)}
                      className="text-red-600 hover:text-red-700 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm text-center py-8">
                  No blackout dates set. Add dates when you're not available.
                </p>
              )}
            </div>

            {/* Add Blackout Date Form */}
            {showAddBlackout && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-medium text-gray-900 mb-3">Add Blackout Date</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={newBlackoutDate.date}
                      onChange={(e) => setNewBlackoutDate({...newBlackoutDate, date: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                    <input
                      type="text"
                      value={newBlackoutDate.reason}
                      onChange={(e) => setNewBlackoutDate({...newBlackoutDate, reason: e.target.value})}
                      placeholder="e.g., Vacation, Holiday, Personal"
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={addBlackoutDate}
                      disabled={!newBlackoutDate.date || !newBlackoutDate.reason}
                      className="pf-btn-primary px-3 py-2 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed text-sm flex-1"
                    >
                      Add Date
                    </button>
                    <button
                      onClick={() => {
                        setShowAddBlackout(false);
                        setNewBlackoutDate({ date: '', reason: '' });
                      }}
                      className="bg-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-400 text-sm flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">Availability Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-blue-900">Available Days</p>
              <p className="text-blue-700">
                {Object.values(weeklySchedule).filter(day => day.available).length} days per week
              </p>
            </div>
            <div>
              <p className="font-medium text-blue-900">Total Hours</p>
              <p className="text-blue-700">
                {Object.values(weeklySchedule)
                  .filter(day => day.available)
                  .reduce((total, day) => {
                    const from = new Date(`2000-01-01T${day.from}:00`);
                    const to = new Date(`2000-01-01T${day.to}:00`);
                    return total + (to - from) / (1000 * 60 * 60);
                  }, 0)
                } hours per week
              </p>
            </div>
            <div>
              <p className="font-medium text-blue-900">Blackout Dates</p>
              <p className="text-blue-700">{blackoutDates.length} dates blocked</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Availability;
